import { useEffect, useState } from 'react'
import { Col, Container, Dropdown, Image, Row } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import KAlert from './components/KAlert'
import KLayerNavigation from './components/KLayerNavigation'
import HomeStatsCards from './components/HomeStatsCards'
import ServiceUsageDonutChart from './components/ServiceUsageDonutChart'
import LastActivitiesCard from './components/LastActivitiesCard'
import {
  apiRequest,
  getCompanyInfo,
  getUserAccess,
  isPiiEncryptionEnabled,
  isUserLoggedIn,
  setCompanyInfo,
} from './helpers'
import { changeLocale, locales, localeFlagCodes, t } from './locale'
import { CompanyInfo, UserAccess } from './react-app-env'
import { getServices } from './helpers/services.helper'
import { CountryFlag } from './components/CountryFlag'

function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const isHomePage = location.pathname === '/'
  const subdomain: string = window.subdomain ?? ''
  const userAccess: UserAccess = getUserAccess()

  const [kAlert, setKAlert] = useState(window.kAlert)
  const [showKAlert, setShowKAlert] = useState(false)
  const [companyName, setCompanyName] = useState(getCompanyInfo()?.name)
  const [serviceCounts, setServiceCounts] = useState<Record<string, number> | null>(null)
  const [transparenzCounts, setTransparenzCounts] = useState<Record<string, number> | null>(null)
  const [identityCounts, setIdentityCounts] = useState<Record<string, number> | null>(null)
  const [amlRecords, setAmlRecords] = useState<Record<string, number> | null>(null)
  const [dsSignCounts, setDsSignCounts] = useState<Record<string, number> | null>(null)

  const shouldHidePaths = ['/company/create', '/user/login', '/user/twofa-verify']
  const hideHeaderFooter = shouldHidePaths.some(p => location.pathname.includes(p))

  function handleLocaleChange(locale: string) {
    changeLocale(locale)
  }

  useEffect(() => {
    const onShow = () => {
      setKAlert(window.kAlert)
      setShowKAlert(true)
    }
    const onHide = () => {
      setShowKAlert(false)
    }

    window.addEventListener('showKAlert', onShow)
    window.addEventListener('hideKAlert', onHide)

    return () => {
      window.removeEventListener('showKAlert', onShow)
      window.removeEventListener('hideKAlert', onHide)
    }
  }, [])

  useEffect(() => {
    if (!!subdomain && !companyName && isUserLoggedIn())
      fetch(`${process.env.REACT_APP_API_URL}/company/info`)
        .then(r => r.json())
        .then((r: CompanyInfo) => {
          setCompanyInfo(r)
          setCompanyName(r.name)
        })
  }, [companyName, subdomain])

  useEffect(() => {
    if (!!subdomain && isHomePage && isUserLoggedIn())
      apiRequest(`${process.env.REACT_APP_API_URL}/schufa/kyc/count`, 'POST').then(r => {
        if (r?.ok) setServiceCounts(r.counts)
      })
  }, [subdomain, isHomePage])

  useEffect(() => {
    if (!!subdomain && isHomePage && isUserLoggedIn()) {
      apiRequest(`${process.env.REACT_APP_API_URL}/transparenzregister/requests/all`, 'GET').then(
        (r: { company_name: string; status: string }[]) => {
          if (!Array.isArray(r)) return
          const counts: Record<string, number> = {}
          r.forEach(({ status }) => {
            counts[status] = (counts[status] ?? 0) + 1
          })
          setTransparenzCounts(counts)
        }
      )

      apiRequest(`${process.env.REACT_APP_API_URL}/sign/requests/all`, 'GET').then(
        (r: { company_name: string; status: string }[]) => {
          if (!Array.isArray(r)) return
          const counts: Record<string, number> = {}
          r.forEach(({ status }) => {
            counts[status] = (counts[status] ?? 0) + 1
          })
          setDsSignCounts(counts)
        }
      )
      apiRequest(`${process.env.REACT_APP_API_URL}/identity/all`, 'GET').then(
        (r: { ok: boolean; identities: any[] }) => {
          if (!r.ok || !Array.isArray(r.identities)) return
          const counts: Record<string, number> = {}
          r.identities.forEach(({ status }) => {
            counts[status] = (counts[status] ?? 0) + 1
          })
          setIdentityCounts(counts)
        }
      )
      apiRequest(`${process.env.REACT_APP_API_URL}/aml/all`, 'GET').then(
        (r: { ok: boolean; amlRecords: any[] }) => {
          if (!r.ok || !Array.isArray(r.amlRecords)) return
          const counts: Record<string, number> = {}
          r.amlRecords.forEach(({ orders }) => {
            if (Array.isArray(orders)) {
              orders.forEach(({ status }: { status: string }) => {
                if (status) counts[status] = (counts[status] ?? 0) + 1
              })
            }
          })
          setAmlRecords(counts)
        }
      )
    }
  }, [subdomain, isHomePage])

  return (
    // <div style={{ backgroundColor: '#f0f7ff', minHeight: '100vh' }}>
    <div>
      {!hideHeaderFooter && (
        <>
          <nav
            className={`${isHomePage && !subdomain ? 'bg-secondary' : 'bg-white'} border-bottom py-1 mb-5`}
          >
            <div className='container-lg d-flex align-items-center justify-content-between'>
              <div className='d-flex'>
                <Col lg={10}>
                  <Link
                    to='/'
                    className='d-flex align-items-center link-body-emplhasis text-decoration-none p-3'
                  >
                    <Image fluid src='/KYC_logo_black.png' className='navbar-logo' />
                  </Link>
                </Col>
              </div>

              <div className='d-flex align-items-center gap-3'>
                {isHomePage && !subdomain && (
                  <>
                    <Button variant='light' onClick={() => navigate('/login')}>
                      {t('login')}
                    </Button>
                    <Button
                      variant='primary'
                      style={{ textTransform: 'none' }}
                      onClick={() => navigate('/company/create')}
                    >
                      {t('register')}
                    </Button>
                  </>
                )}

                <Dropdown className='language-dropdown text-end'>
                  <Dropdown.Toggle variant='none' aria-expanded='false'>
                    {t('language')}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className='text-small'>
                    {Object.entries(locales).map(([locale, language]) => (
                      <Dropdown.Item
                        id={'dd-item-locale-' + locale}
                        key={locale}
                        onClick={() => {
                          handleLocaleChange(locale)
                        }}
                      >
                        <CountryFlag code={localeFlagCodes[locale] ?? locale} />
                        <span className='ms-2'>{language}</span>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </nav>
        </>
      )}

      {hideHeaderFooter && (
        <div className='d-flex justify-content-end'>
          <Dropdown className='language-dropdown text-end'>
            <Dropdown.Toggle variant='none' aria-expanded='false'>
              {t('language')}
            </Dropdown.Toggle>
            <Dropdown.Menu className='text-small'>
              {Object.entries(locales).map(([locale, language]) => (
                <Dropdown.Item
                  id={'dd-item-locale-' + locale}
                  key={locale}
                  onClick={() => {
                    handleLocaleChange(locale)
                  }}
                >
                  {language}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )}
      <div
        className={
          hideHeaderFooter ? 'justify-content-center align-items-center w-50' : 'container-fluid'
        }
      >
        <Row className={!hideHeaderFooter ? 'mt-2' : 'w-100'}>
          <KAlert show={showKAlert} variant={kAlert?.variant} message={kAlert?.message} />
        </Row>
      </div>

      {!!subdomain && isHomePage && (
        // Home-only: this Container holds the dashboard sidebar + cards. It must
        // stay gated on isHomePage so other routes render <Outlet /> directly -
        // otherwise the empty Row/sidebar column plus this paddingBottom leave a
        // ~90px blank band above every form.
        // paddingBottom keeps content clear of the <footer> below, which is
        // pinned with fixed-bottom (position: fixed) - without this, a tall
        // expanded sidebar (all menu sections open) ends up with its last items
        // rendered underneath the footer's fixed strip, making them unclickable.
        // 90px comfortably clears the footer's rendered height (border + py-3
        // padding + one line of text).
        <Container style={{ paddingBottom: '90px' }}>
          <Row>
            <div className='sidebar col-md-3 col-lg-2 p-0'>
              {isHomePage && <KLayerNavigation logged={isUserLoggedIn()} />}
            </div>

            {isHomePage && isUserLoggedIn() && (
              <main className='col-lg-10 px-md-4 mb-5'>
                <Row className='g-4 mb-2'>
                  <Col lg={8}>
                    <div className='rounded p-2 h-100'>
                      <h5 className='fw-bold text-draft mb-2'>{t('welcome') + userAccess.name}</h5>
                      <p className='text-muted'>{t('homeIntro')}</p>
                    </div>
                  </Col>
                </Row>
                <Row className='g-4 ms-2 mb-2'>
                  <HomeStatsCards />
                </Row>

                <Row className='g-4 mb-2'>
                  <Col lg={6}>
                    <ServiceUsageDonutChart />
                  </Col>
                  <Col lg={6}>
                    <LastActivitiesCard />
                  </Col>
                </Row>

                <div className='border rounded bg-white shadow-sm mb-4'>
                  <div className='table-responsive'>
                    <table className='table table-sm table-hover mb-0'>
                      <thead>
                        <tr>
                          <th className='text-primary smaller px-3'>{t('service')}</th>
                          <th className='text-primary smaller px-3'>{t('description')}</th>
                          <th className='text-primary smaller text-end px-3'>
                            {t('numberChecks')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getServices()
                          .filter(
                            row =>
                              !(
                                isPiiEncryptionEnabled() &&
                                (row.key === 'dsIdentity' || row.key === 'dsSign')
                              )
                          )
                          .map(row => (
                            <tr key={row.key}>
                              <td className='smaller text-draft px-3'>{t(row.label)}</td>
                              <td className='smaller text-draft px-3'>{t(row.desc)}</td>
                              <td className='smaller text-end px-3'>
                                <span className='fw-medium text-primary'>
                                  {serviceCounts ? (serviceCounts[row.key] ?? 0) : '—'}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ds identity */}
                <div className='border rounded bg-white shadow-sm mb-4'>
                  <div className='px-3 py-2 border-bottom'>
                    <span className='fw-semibold text-draft small'>
                      {t('identityRequestsByStatus')}
                    </span>
                  </div>
                  <div className='table-responsive'>
                    <table className='table table-sm table-hover mb-0'>
                      <thead>
                        <tr>
                          <th className='text-primary smaller px-3'>{t('Status')}</th>
                          <th className='text-primary smaller text-end px-3'>
                            {t('numberChecks')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'assigned', label: t('identityStatusAssigned') },
                          { key: 'inProcess', label: t('identityStatusInProcess') },
                          { key: 'finished', label: t('identityStatusFinished') },
                        ]
                          .filter(row => identityCounts?.[row.key])
                          .map(row => (
                            <tr key={row.key}>
                              <td className='smaller text-draft px-3'>{row.label}</td>
                              <td className='smaller text-end px-3'>
                                <span className='fw-medium text-primary'>
                                  {identityCounts![row.key]}
                                </span>
                              </td>
                            </tr>
                          ))}
                        {transparenzCounts && Object.keys(transparenzCounts).length === 0 && (
                          <tr>
                            <td colSpan={2} className='smaller text-muted text-center px-3'>
                              {t('noData')}
                            </td>
                          </tr>
                        )}
                        {!transparenzCounts && (
                          <tr>
                            <td colSpan={2} className='smaller text-muted text-center px-3'>
                              —
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/*transparenzregister*/}
                <div className='border rounded bg-white shadow-sm mb-4'>
                  <div className='px-3 py-2 border-bottom'>
                    <span className='fw-semibold text-draft small'>
                      {t('transparenzRequestsByStatus')}
                    </span>
                  </div>
                  <div className='table-responsive'>
                    <table className='table table-sm table-hover mb-0'>
                      <thead>
                        <tr>
                          <th className='text-primary smaller px-3'>{t('Status')}</th>
                          <th className='text-primary smaller text-end px-3'>
                            {t('numberChecks')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'inProcess', label: t('trStatusInProcess') },
                          { key: 'finished', label: t('trStatusSucceeded') },
                        ]
                          .filter(row => transparenzCounts?.[row.key])
                          .map(row => (
                            <tr key={row.key}>
                              <td className='smaller text-draft px-3'>{row.label}</td>
                              <td className='smaller text-end px-3'>
                                <span className='fw-medium text-primary'>
                                  {transparenzCounts![row.key]}
                                </span>
                              </td>
                            </tr>
                          ))}
                        {transparenzCounts && Object.keys(transparenzCounts).length === 0 && (
                          <tr>
                            <td colSpan={2} className='smaller text-muted text-center px-3'>
                              {t('noData')}
                            </td>
                          </tr>
                        )}
                        {!transparenzCounts && (
                          <tr>
                            <td colSpan={2} className='smaller text-muted text-center px-3'>
                              —
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* handelsregister */}
                <div className='border rounded bg-white shadow-sm mb-4'>
                  <div className='px-3 py-2 border-bottom'>
                    <span className='fw-semibold text-draft small'>{t('amlRequestsByStatus')}</span>
                  </div>
                  <div className='table-responsive'>
                    <table className='table table-sm table-hover mb-0'>
                      <thead>
                        <tr>
                          <th className='text-primary smaller px-3'>{t('Status')}</th>
                          <th className='text-primary smaller text-end px-3'>
                            {t('numberChecks')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'pending', label: t('amlStatusPending') },
                          { key: 'finished', label: t('amlStatusDone') },
                          { key: 'failed', label: t('amlStatusFailed') },
                        ]
                          .filter(row => amlRecords?.[row.key])
                          .map(row => (
                            <tr key={row.key}>
                              <td className='smaller text-draft px-3'>{row.label}</td>
                              <td className='smaller text-end px-3'>
                                <span className='fw-medium text-primary'>
                                  {amlRecords![row.key]}
                                </span>
                              </td>
                            </tr>
                          ))}
                        {amlRecords && Object.keys(amlRecords).length === 0 && (
                          <tr>
                            <td colSpan={2} className='smaller text-muted text-center px-3'>
                              {t('noData')}
                            </td>
                          </tr>
                        )}
                        {!amlRecords && (
                          <tr>
                            <td colSpan={2} className='smaller text-muted text-center px-3'>
                              —
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ds sign */}
                <div className='border rounded bg-white shadow-sm mb-4'>
                  <div className='px-3 py-2 border-bottom'>
                    <span className='fw-semibold text-draft small'>
                      {t('dsSignRequestsByStatus')}
                    </span>
                  </div>
                  <div className='table-responsive'>
                    <table className='table table-sm table-hover mb-0'>
                      <thead>
                        <tr>
                          <th className='text-primary smaller px-3'>{t('Status')}</th>
                          <th className='text-primary smaller text-end px-3'>
                            {t('numberChecks')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'to_sign', label: t('dsSignStatusInProcess') },
                          { key: 'pending', label: t('dsSignStatusInProcess') },
                          { key: 'finished', label: t('dsSignTrStatusSucceeded') },
                          { key: 'canceled', label: t('dsSignTrStatusCanceled') },
                        ]
                          .filter(row => dsSignCounts?.[row.key])
                          .map(row => (
                            <tr key={row.key}>
                              <td className='smaller text-draft px-3'>{row.label}</td>
                              <td className='smaller text-end px-3'>
                                <span className='fw-medium text-primary'>
                                  {dsSignCounts![row.key]}
                                </span>
                              </td>
                            </tr>
                          ))}
                        {dsSignCounts && Object.keys(dsSignCounts).length === 0 && (
                          <tr>
                            <td colSpan={2} className='smaller text-muted text-center px-3'>
                              {t('noData')}
                            </td>
                          </tr>
                        )}
                        {!dsSignCounts && (
                          <tr>
                            <td colSpan={2} className='smaller text-muted text-center px-3'>
                              —
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </main>
            )}
          </Row>
        </Container>
      )}
      {/* The footer below is `fixed-bottom` (position: fixed), so routed pages
          need bottom clearance or their last content ends up underneath it and
          unreachable - the home Container handles its own spacing above. */}
      <div style={!hideHeaderFooter ? { paddingBottom: '90px' } : undefined}>
        <Outlet />
      </div>
      {!hideHeaderFooter && (
        <Container fluid className='px-sm-5 border-top fixed-bottom bg-white'>
          <footer className='footer border-top border-light px-0 py-3'>
            <Container fluid>
              <div className='d-flex justify-content-center'>
                <div className='text-center text-draft small'>
                  © 2026
                  <span className='text-primary'> DS</span>|KYC by
                  <Link
                    to='https://data-security.one'
                    className='text-draft link-offset-2 link-offset-3-hover 
                                    link-underline link-underline-opacity-0 
                                    link-underline-opacity-75-hover d-inline'
                  >
                    {' '}
                    DATA Security {window.location.hostname.endsWith('.ch') ? 'AG' : 'GmbH'}
                  </Link>{' '}
                  |
                  <Link
                    to='https://data-security.one/datenschutzhinweise/'
                    className='text-draft link-offset-2 link-offset-3-hover
                                    link-underline link-underline-opacity-0
                                    link-underline-opacity-75-hover d-inline'
                  >
                    {' '}
                    Datenschutz
                  </Link>
                  <Link
                    to={
                      window.location.hostname.endsWith('.ch')
                        ? 'https://data-security.ch/de/impressum'
                        : 'https://data-security.one/impressum/'
                    }
                    className='text-draft link-offset-2 link-offset-3-hover
                                    link-underline link-underline-opacity-0 
                                    link-underline-opacity-75-hover d-inline'
                  >
                    {' '}
                    - Impressum
                  </Link>
                </div>
              </div>
            </Container>
          </footer>
        </Container>
      )}
    </div>
  )
}

export default Layout
